"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePostHog } from "posthog-js/react";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Check, XIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

/** Wait this long after a mailto click before assuming no mail app opened. */
const FOCUS_LOSS_TIMEOUT_MS = 800;
/** How long the "Copied!" confirmation stays visible. */
const COPIED_TIMEOUT_MS = 2500;

/** Pull the address out of a mailto: href, stripping the scheme and any query (?subject=…). */
function parseMailtoAddress(href: string): string {
  try {
    const withoutScheme = href.replace(/^mailto:/i, "");
    const address = withoutScheme.split("?")[0] ?? "";
    return decodeURIComponent(address).trim();
  } catch {
    return (
      href
        .replace(/^mailto:/i, "")
        .split("?")[0]
        ?.trim() ?? ""
    );
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }

  // Fallback for insecure contexts / older browsers where the async API is unavailable.
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Global, dependency-free fallback for mailto: links.
 *
 * When a visitor clicks any `a[href^="mailto:"]` and no mail client takes over
 * (the window never loses focus within {@link FOCUS_LOSS_TIMEOUT_MS}), we show a
 * small bottom-right popup with the address and a copy button — instead of the
 * click silently doing nothing. If a mail app does open, the window blurs and
 * the popup is never shown.
 */
export function MailtoFallback() {
  const posthog = usePostHog();
  const reduceMotion = useReducedMotion();

  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setMounted(true), []);

  const closePopup = useCallback(() => {
    setEmail(null);
    setCopied(false);
    if (copiedTimerRef.current) {
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
  }, []);

  // Detect mailto clicks and decide whether a mail client actually opened.
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      // Ignore modified / non-primary clicks and clicks another handler already cancelled.
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target as Element | null;
      const link = target?.closest?.('a[href^="mailto:"]') as HTMLAnchorElement | null;
      if (!link) return;

      const address = parseMailtoAddress(link.getAttribute("href") ?? "");
      // Nothing useful to copy (e.g. a "share via email" link with no recipient).
      if (!address) return;

      // Let the browser try to open the mail handler — we only observe the outcome.
      let mailAppOpened = false;

      const markOpened = () => {
        mailAppOpened = true;
      };
      const onVisibility = () => {
        if (document.hidden) markOpened();
      };

      window.addEventListener("blur", markOpened, { once: true });
      window.addEventListener("pagehide", markOpened, { once: true });
      document.addEventListener("visibilitychange", onVisibility);

      window.setTimeout(() => {
        window.removeEventListener("blur", markOpened);
        window.removeEventListener("pagehide", markOpened);
        document.removeEventListener("visibilitychange", onVisibility);

        if (mailAppOpened) return;

        setCopied(false);
        setEmail(address);
        posthog?.capture("mailto_fallback_shown", { email: address });
      }, FOCUS_LOSS_TIMEOUT_MS);
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [posthog]);

  // Escape to close + click-outside to close, only while the popup is open.
  useEffect(() => {
    if (!email) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closePopup();
    }
    function onPointerDown(event: PointerEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        closePopup();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [email, closePopup]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!email) return;
    const ok = await copyToClipboard(email);
    if (!ok) return;

    setCopied(true);
    posthog?.capture("mailto_email_copied", { email });

    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), COPIED_TIMEOUT_MS);
  }, [email, posthog]);

  if (!mounted) return null;

  const enter = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 16, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 16, scale: 0.98 },
      };

  return createPortal(
    <AnimatePresence>
      {email && (
        <div className="fixed right-4 bottom-4 z-100 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2 sm:right-6 sm:bottom-6">
          <motion.div
            ref={popupRef}
            role="dialog"
            aria-label="Email address fallback"
            initial={enter.initial}
            animate={enter.animate}
            exit={enter.exit}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            drag="x"
            dragSnapToOrigin
            onDragEnd={(_event, info) => {
              // Swipe the popup out — far enough or fast enough dismisses it.
              if (Math.abs(info.offset.x) > 80 || Math.abs(info.velocity.x) > 400) {
                closePopup();
              }
            }}
            className={cn(
              "bg-popover text-popover-foreground border-border relative w-88 max-w-full",
              "cursor-grab touch-pan-y rounded-xl border bg-clip-padding p-4 shadow-lg select-none active:cursor-grabbing"
            )}
          >
            <button
              type="button"
              onClick={closePopup}
              aria-label="Dismiss"
              className="text-muted-foreground hover:bg-muted hover:text-foreground absolute top-2.5 right-2.5 inline-flex size-7 items-center justify-center rounded-md transition-colors"
            >
              <XIcon className="size-4" />
            </button>

            <p className="text-foreground pr-6 text-sm leading-relaxed">
              Failed to open your default mail app. Email us at{" "}
              <span className="text-foreground font-medium break-all">{email}</span>
            </p>

            <Button variant="outline" size="sm" onClick={handleCopy} className="mt-3 w-full">
              {copied ? "Copied!" : "Copy email address"}
            </Button>
          </motion.div>

          <AnimatePresence>
            {copied && (
              <motion.div
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                transition={{ duration: 0.18 }}
                aria-live="polite"
                className="bg-popover text-muted-foreground border-border flex w-88 max-w-full items-center gap-2 rounded-lg border bg-clip-padding px-3 py-2.5 text-sm shadow-lg"
              >
                <Check className="text-brand-green size-4 shrink-0" />
                Copied email address to clipboard.
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
